import { clamp, lerp, seg, smooth } from '../../../motion'

/**
 * Formationen für das Partikelfeld. Jede liefert für `n` Partikel Zielpunkte
 * in Pixeln (`pos`, x/y im Wechsel) und optional Deckkraft je Partikel.
 * Dynamische Formationen (`dyn`) rechnen ihre Punkte in jedem Frame neu,
 * abhängig von Zeit und Fortschritt ihrer Section.
 */

// Deterministischer Zufall, damit Formationen bei jedem Aufbau gleich aussehen
export function rng(seed = 1) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const out = (n) => ({
  pos: new Float32Array(n * 2),
  alpha: new Float32Array(n).fill(1)
})

// Punkte aus einer Zeichnung auf einer Offscreen-Leinwand ziehen
function sample(w, h, n, draw, seed) {
  const canvas = document.createElement('canvas')
  const scale = 0.5
  canvas.width = Math.max(1, Math.round(w * scale))
  canvas.height = Math.max(1, Math.round(h * scale))
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.scale(scale, scale)
  ctx.fillStyle = '#000'
  draw(ctx)
  const { data, width, height } = ctx.getImageData(
    0,
    0,
    canvas.width,
    canvas.height
  )
  const pts = []
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      if (data[(y * width + x) * 4 + 3] > 128) pts.push(x / scale, y / scale)
    }
  }
  const r = rng(seed)
  const o = out(n)
  const count = pts.length / 2
  for (let i = 0; i < n; i++) {
    if (!count) {
      o.pos[i * 2] = w / 2
      o.pos[i * 2 + 1] = h / 2
      continue
    }
    const k = Math.floor(r() * count)
    o.pos[i * 2] = pts[k * 2] + (r() - 0.5) * 3
    o.pos[i * 2 + 1] = pts[k * 2 + 1] + (r() - 0.5) * 3
  }
  return o
}

function heartPath(ctx, x, y, s) {
  ctx.beginPath()
  ctx.moveTo(x, y + s * 0.35)
  ctx.bezierCurveTo(
    x - s * 0.1,
    y + s * 0.22,
    x - s * 0.5,
    y,
    x - s * 0.5,
    y - s * 0.2
  )
  ctx.bezierCurveTo(
    x - s * 0.5,
    y - s * 0.45,
    x - s * 0.18,
    y - s * 0.55,
    x,
    y - s * 0.3
  )
  ctx.bezierCurveTo(
    x + s * 0.18,
    y - s * 0.55,
    x + s * 0.5,
    y - s * 0.45,
    x + s * 0.5,
    y - s * 0.2
  )
  ctx.bezierCurveTo(x + s * 0.5, y, x + s * 0.1, y + s * 0.22, x, y + s * 0.35)
  ctx.fill()
}

const BUILDERS = {
  text(w, h, n, { text, x, y, size, weight = 700, seed = 3 }) {
    return sample(
      w,
      h,
      n,
      (ctx) => {
        ctx.font = `${weight} ${size}px "Fira Sans", sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        const lines = String(text).split('\n')
        lines.forEach((line, i) =>
          ctx.fillText(line, x, y + (i - (lines.length - 1) / 2) * size * 0.95)
        )
      },
      seed
    )
  },

  heart(w, h, n, { x, y, size, seed = 5 }) {
    return sample(w, h, n, (ctx) => heartPath(ctx, x, y, size), seed)
  },

  scatter(w, h, n, { alpha = 0.5, seed = 7, top = 0, bottom = 1 }) {
    const r = rng(seed)
    const o = out(n)
    for (let i = 0; i < n; i++) {
      o.pos[i * 2] = r() * w
      o.pos[i * 2 + 1] = lerp(top, bottom, r()) * h
      o.alpha[i] = alpha * (0.4 + 0.6 * r())
    }
    return o
  },

  ring(w, h, n, { x, y, r: radius, spread = 0.12, alpha = 1, seed = 9 }) {
    const r = rng(seed)
    const o = out(n)
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.05
      const d = radius * (1 + (r() - 0.5) * spread * 2)
      o.pos[i * 2] = x + Math.cos(a) * d
      o.pos[i * 2 + 1] = y + Math.sin(a) * d
      o.alpha[i] = alpha
    }
    return o
  },

  rings(w, h, n, { centers, r: radius, spread = 0.1, seed = 11 }) {
    const r = rng(seed)
    const o = out(n)
    for (let i = 0; i < n; i++) {
      const [cx, cy] = centers[i % centers.length]
      const a = r() * Math.PI * 2
      const d = radius * (1 + (r() - 0.5) * spread * 2)
      o.pos[i * 2] = cx + Math.cos(a) * d
      o.pos[i * 2 + 1] = cy + Math.sin(a) * d
    }
    return o
  },

  // Ein Lebenslauf als Umriss mit Foto und Textzeilen
  cv(w, h, n, { x, y, width, height, seed = 13 }) {
    const r = rng(seed)
    const o = out(n)
    const left = x - width / 2
    const top = y - height / 2
    const lines = [
      [0.42, 0.12, 0.5],
      [0.42, 0.2, 0.36],
      [0.1, 0.38, 0.8],
      [0.1, 0.46, 0.7],
      [0.1, 0.54, 0.76],
      [0.1, 0.68, 0.8],
      [0.1, 0.76, 0.6],
      [0.1, 0.84, 0.72]
    ]
    for (let i = 0; i < n; i++) {
      const kind = r()
      let px
      let py
      if (kind < 0.36) {
        const t = r() * 2 * (width + height)
        if (t < width) [px, py] = [left + t, top]
        else if (t < width + height) [px, py] = [left + width, top + t - width]
        else if (t < 2 * width + height)
          [px, py] = [left + width - (t - width - height), top + height]
        else [px, py] = [left, top + height - (t - 2 * width - height)]
      } else if (kind < 0.48) {
        const a = r() * Math.PI * 2
        const d = Math.sqrt(r()) * width * 0.13
        ;[px, py] = [
          left + width * 0.22 + Math.cos(a) * d,
          top + height * 0.16 + Math.sin(a) * d
        ]
      } else {
        const [lx, ly, lw] = lines[Math.floor(r() * lines.length)]
        ;[px, py] = [
          left + width * (lx + r() * lw),
          top + height * ly + (r() - 0.5) * 4
        ]
      }
      o.pos[i * 2] = px
      o.pos[i * 2 + 1] = py
    }
    return o
  },

  grid(w, h, n, { x, y, width, height }) {
    const o = out(n)
    const cols = Math.max(1, Math.round(Math.sqrt((n * width) / height)))
    const rows = Math.ceil(n / cols)
    for (let i = 0; i < n; i++) {
      const c = i % cols
      const rr = Math.floor(i / cols)
      o.pos[i * 2] = x - width / 2 + (c + 0.5) * (width / cols)
      o.pos[i * 2 + 1] = y - height / 2 + (rr + 0.5) * (height / rows)
    }
    return o
  },

  frame(w, h, n, { inset = 24, seed = 15 }) {
    const r = rng(seed)
    const o = out(n)
    const W = w - inset * 2
    const H = h - inset * 2
    for (let i = 0; i < n; i++) {
      const t = r() * 2 * (W + H)
      const j = (r() - 0.5) * 18
      let px
      let py
      if (t < W) [px, py] = [inset + t, inset + j]
      else if (t < W + H) [px, py] = [inset + W + j, inset + t - W]
      else if (t < 2 * W + H)
        [px, py] = [inset + W - (t - W - H), inset + H + j]
      else [px, py] = [inset + j, inset + H - (t - 2 * W - H)]
      o.pos[i * 2] = px
      o.pos[i * 2 + 1] = py
      o.alpha[i] = 0.7
    }
    return o
  },

  // Links Chaos, rechts Ordnung. Die Grenze wandert mit dem Fortschritt der Section
  chaosOrder(w, h, n, { at = [0.1, 0.6], seed = 17 }) {
    const r = rng(seed)
    const cols = Math.round(Math.sqrt((n * w) / h))
    const rows = Math.ceil(n / cols)
    const grid = new Float32Array(n * 2)
    const chaos = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
      grid[i * 2] = ((i % cols) + 0.5) * (w / cols)
      grid[i * 2 + 1] =
        h * 0.08 + (Math.floor(i / cols) + 0.5) * ((h * 0.84) / rows)
      chaos[i * 3] = r() * w
      chaos[i * 3 + 1] = r() * h
      chaos[i * 3 + 2] = r() * Math.PI * 2
    }
    const o = out(n)
    o.grey = new Uint8Array(n)
    o.dyn = (t, l) => {
      const split = lerp(-0.05 * w, 1.05 * w, smooth(seg(l, at[0], at[1])))
      for (let i = 0; i < n; i++) {
        const gx = grid[i * 2]
        if (gx < split) {
          o.pos[i * 2] = gx
          o.pos[i * 2 + 1] = grid[i * 2 + 1]
          o.alpha[i] = 0.95
          o.grey[i] = 0
        } else {
          const ph = chaos[i * 3 + 2]
          o.pos[i * 2] = chaos[i * 3] + Math.sin(t * 0.9 + ph) * 26
          o.pos[i * 2 + 1] =
            chaos[i * 3 + 1] + Math.cos(t * 0.7 + ph * 1.3) * 26
          o.alpha[i] = 0.45
          o.grey[i] = 1
        }
      }
    }
    return o
  },

  // Bewerbungsstrom: links grau, am Tor (gateX) wird gemessen, nur Passende ziehen hell weiter
  stream(w, h, n, { y, band, gateX, pass = 0.22, seed = 19 }) {
    const r = rng(seed)
    const base = new Float32Array(n * 4)
    for (let i = 0; i < n; i++) {
      base[i * 4] = r()
      base[i * 4 + 1] = (r() - 0.5) * 2
      base[i * 4 + 2] = 0.6 + r() * 0.8
      base[i * 4 + 3] = r() < pass ? 1 : 0
    }
    const o = out(n)
    o.grey = new Uint8Array(n)
    o.dyn = (t) => {
      const span = w * 1.3
      for (let i = 0; i < n; i++) {
        const x =
          ((base[i * 4] * span + t * 70 * base[i * 4 + 2]) % span) - w * 0.15
        const off = base[i * 4 + 1]
        let py = y + off * band + Math.sin(t * 1.3 + i) * 4
        let a = 0.55
        let grey = 1
        if (x > gateX) {
          const d = x - gateX
          if (base[i * 4 + 3]) {
            py = y + off * band * Math.max(0.25, 1 - d / 260)
            a = 1
            grey = 0
          } else {
            py += d * 0.55
            a = clamp(0.55 - d / 420)
          }
        }
        o.pos[i * 2] = x
        o.pos[i * 2 + 1] = py
        o.alpha[i] = a
        o.grey[i] = grey
      }
    }
    return o
  },

  // Kugel aus Punkten (Fibonacci), dreht sich langsam
  sphere(w, h, n, { x, y, r: radius }) {
    const o = out(n)
    const pts = new Float32Array(n * 3)
    const golden = Math.PI * (3 - Math.sqrt(5))
    for (let i = 0; i < n; i++) {
      const yy = 1 - (i / (n - 1)) * 2
      const rad = Math.sqrt(1 - yy * yy)
      const th = golden * i
      pts[i * 3] = Math.cos(th) * rad
      pts[i * 3 + 1] = yy
      pts[i * 3 + 2] = Math.sin(th) * rad
    }
    o.dyn = (t) => {
      const a = t * 0.25
      const ca = Math.cos(a)
      const sa = Math.sin(a)
      const tilt = 0.35
      for (let i = 0; i < n; i++) {
        const px = pts[i * 3] * ca + pts[i * 3 + 2] * sa
        const pz = -pts[i * 3] * sa + pts[i * 3 + 2] * ca
        const py = pts[i * 3 + 1] * Math.cos(tilt) - pz * Math.sin(tilt)
        const z = pts[i * 3 + 1] * Math.sin(tilt) + pz * Math.cos(tilt)
        o.pos[i * 2] = x + px * radius
        o.pos[i * 2 + 1] = y + py * radius
        o.alpha[i] = 0.25 + 0.75 * ((z + 1) / 2)
      }
    }
    return o
  },

  // Ring, der sich dreht (Halo ums Smartphone)
  orbit(w, h, n, { x, y, r: radius, spread = 0.25, seed = 21 }) {
    const r = rng(seed)
    const base = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
      base[i * 3] = r() * Math.PI * 2
      base[i * 3 + 1] = radius * (1 + (r() - 0.5) * spread * 2)
      base[i * 3 + 2] = 0.08 + r() * 0.18
    }
    const o = out(n)
    o.dyn = (t) => {
      for (let i = 0; i < n; i++) {
        const a = base[i * 3] + t * base[i * 3 + 2]
        o.pos[i * 2] = x + Math.cos(a) * base[i * 3 + 1]
        o.pos[i * 2 + 1] = y + Math.sin(a) * base[i * 3 + 1] * 0.92
        o.alpha[i] = 0.8
      }
    }
    return o
  }
}

const BLOOM = {
  scatter: 1,
  stream: 0.6,
  orbit: 0.6,
  chaosOrder: 0.5,
  ring: 0.3,
  rings: 0.3,
  sphere: 0.2
}

export function build(spec, w, h, n) {
  const make = BUILDERS[spec.type]
  if (!make) throw new Error(`Unbekannte Formation ${spec.type}`)
  const f = make(w, h, n, spec)
  // `dot` skaliert die Partikelgröße, Schrift und Herz brauchen feinere Punkte.
  // `bloom` lässt die Formen in lockeren Formationen wachsen
  const fine = spec.type === 'text' || spec.type === 'heart'
  const tight =
    spec.type === 'cv' || spec.type === 'grid' || spec.type === 'frame'
  f.size = spec.dot ?? (fine ? 0.62 : tight ? 0.8 : 1)
  f.bloom = spec.bloom ?? BLOOM[spec.type] ?? 0
  f.opacity = spec.opacity ?? 1
  f.tint = spec.tint
  return f
}
